import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addItem } from "../../redux/cart-items";
import { toast } from "react-toastify";
import {
  Box,
  Checkbox,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from "@mui/material";
import {
  formatUSAmount,
  getAllGuestCabins,
} from "../../utils/helperFunctions/helperFunctions";
import {
  getGuestsAsPerCabin,
  getGuestAge,
} from "../../utils/helperFunctions/supplements";
import { getCabinDetails } from "../../redux/cabin-upgrade";

const AddToCartModal = ({
  bookingDetails,
  setShowAddToCartModal,
  supplement,
  min_qty,
  max_qty,
  child_min_qty,
  child_max_qty,
  _adults,
  _children,
}) => {
  const { booking_id } = bookingDetails;
  const { room_id } = bookingDetails.room_with_guest_count[0];
  const [adults, setAdults] = useState(_adults || []);
  const [children, setChildren] = useState(_children || []);

  const allCabinDetails = useSelector(
    (state) => state.allCabinDetails.cabin_details
  );
  const dispatch = useDispatch();
  const availableGuestCabins = useMemo(
    () => getAllGuestCabins(allCabinDetails, bookingDetails),
    [allCabinDetails, bookingDetails]
  );

  const [selectedCabin, setSelectedCabin] = useState("");

  useEffect(() => {
    setSelectedCabin(
      availableGuestCabins[0]?.category_id +
        "-" +
        availableGuestCabins[0]?.room_id
    );
  }, [availableGuestCabins]);

  useEffect(() => {
    if (room_id !== "" && booking_id !== "" && allCabinDetails?.length === 0) {
      dispatch(
        getCabinDetails({
          room_id: room_id,
          booking_id: booking_id,
        })
      );
    }
  }, [allCabinDetails, booking_id, room_id, availableGuestCabins, dispatch]);

  const increment = (setter, value, guest_id) => {
    const max = setter === setAdults ? max_qty : child_max_qty;
    if (value < max) {
      setter((prev) => {
        const index = prev.findIndex((item) => item.guest_id === guest_id);
        if (index === -1) {
          return [...prev, { guest_id, quantity: value + 1 }];
        } else {
          const newItems = [...prev];
          newItems[index] = { ...newItems[index], quantity: value + 1 };
          return newItems;
        }
      });
    }
  };

  const decrement = (setter, value, guest_id) => {
    const min = setter === setAdults ? min_qty : child_min_qty;
    if (value > min) {
      setter((prev) => {
        const index = prev.findIndex((item) => item.guest_id === guest_id);
        if (index === -1) {
          return [...prev, { guest_id, quantity: value - 1 }];
        } else {
          const newItems = [...prev];
          newItems[index] = { ...newItems[index], quantity: value - 1 };
          return newItems;
        }
      });
    }
  };

  const [guestsAsPerCabin, setGuestsAsPerCabin] = useState([]);

  const custom5_check = () => {
    const adultGuests = guestsAsPerCabin?.filter(
      (guest) => getGuestAge(guest.dob, bookingDetails.sailing_date) > 21
    );
    if (supplement.Selection_method === "custom 5" && adultGuests.length >= 2) {
      const checkedAdults = adults.filter(
        (a) =>
          a.checked && guestsAsPerCabin?.map((g) => g.id).includes(a.guest_id)
      );

      if (checkedAdults.length >= 2 || checkedAdults.length === 0) {
        return true;
      } else {
        toast.error("Please select at least two adults");
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    setGuestsAsPerCabin(getGuestsAsPerCabin(selectedCabin, bookingDetails));
    if (supplement.Selection_method === "per cabin") {
      setGuestsAsPerCabin((prev) => {
        return [
          prev.find(
            (guest) => getGuestAge(guest?.dob, bookingDetails.sailing_date) > 13
          ),
        ];
      });
    }
    if (
      supplement.Selection_method === "custom 5" ||
      supplement.Selection_method === "custom 3" ||
      supplement.Selection_method === "custom 8"
    ) {
      setGuestsAsPerCabin((prev) => {
        return prev.filter(
          (guest) => getGuestAge(guest?.dob, bookingDetails.sailing_date) > 21
        );
      });
    }
  }, [selectedCabin, bookingDetails, supplement]);

  const handleAddToCart = () => {
    if (!custom5_check()) return;
    if (
      adults.filter((a) => a.checked).length === 0 &&
      children.filter((c) => c.checked).length === 0
    ) {
      toast.error("Please select at least one guest");
      return;
    }

    const filteredAdults = adults.filter((item) => item.checked);
    const filteredChildren = children.filter((item) => item.checked);

    setAdults(filteredAdults);
    setChildren(filteredChildren);
    if (bookingDetails.payment_details.due_amount > 0) {
      toast.error("Please complete your cruise payment to add enhancements");
      return;
    }
    dispatch(
      addItem({
        supplement,
        adults: filteredAdults,
        children: filteredChildren,
      })
    );
    setShowAddToCartModal(false);
  };

  const handleCheckboxChange = (guest_id, setter, checked) => {
    if (supplement.Selection_method === "custom 3") {
      setter((prev) => {
        return [
          ...prev,
          ...guestsAsPerCabin.map((guest) => {
            if (
              getGuestAge(guest.dob, bookingDetails.sailing_date) > 21 &&
              checked
            ) {
              return { guest_id: guest.id, quantity: 1, checked };
            }
            return { guest_id: guest.id, quantity: 1, checked: false };
          }),
        ];
      });
    } else {
      setter((prev) => {
        const index = prev.findIndex((item) => item.guest_id === guest_id);
        if (index === -1) {
          return [...prev, { guest_id, quantity: 1, checked }];
        } else {
          const newItems = [...prev];
          newItems[index] = { ...newItems[index], checked };
          return newItems;
        }
      });
    }
  };

  const [checkedSuppDetails, setCheckedSuppDetails] = useState({
    total: 0,
  });

  useEffect(() => {
    setCheckedSuppDetails((prevDetails) => ({
      ...prevDetails,
      total:
        (adults
          .filter((item) => item.checked)
          .reduce((acc, item) => acc + item.quantity, 0) || 0) *
          (supplement?.farecode_price || 0) +
        (children
          .filter((item) => item.checked)
          .reduce((acc, item) => acc + item.quantity, 0) || 0) *
          (supplement?.child?.farecode_price || 0),
      [selectedCabin]: {
        quantity:
          (adults
            .filter(
              (item) =>
                item.checked &&
                guestsAsPerCabin.find((g) => g.id === item.guest_id)
            )
            .reduce((acc, item) => acc + item.quantity, 0) || 0) +
          (children
            .filter(
              (item) =>
                item.checked &&
                guestsAsPerCabin.find((g) => g.id === item.guest_id)
            )
            .reduce((acc, item) => acc + item.quantity, 0) || 0),
        subtotal:
          (adults
            .filter(
              (item) =>
                item.checked &&
                guestsAsPerCabin.find((g) => g.id === item.guest_id)
            )
            .reduce((acc, item) => acc + item.quantity, 0) || 0) *
            (supplement?.farecode_price || 0) +
          (children
            .filter(
              (item) =>
                item.checked &&
                guestsAsPerCabin.find((g) => g.id === item.guest_id)
            )
            .reduce((acc, item) => acc + item.quantity, 0) || 0) *
            (supplement?.child?.farecode_price || 0),
      },
    }));
  }, [adults, children, guestsAsPerCabin, selectedCabin, supplement]);

  return (
    <div>
      <div className="w-full max-w-lg mx-auto mt-10 p-6 relative bg-white shadow-md rounded-md">
        <div className="flex absolute right-2 top-2 justify-end">
          <div
            onClick={() => setShowAddToCartModal(false)}
            className="cursor-pointer text-4xl"
          >
            &times;
          </div>
        </div>
        <h1 className="text-mv-darkblue uppercase font-semibold py-2 text-2xl text-center">
          {supplement.title}
        </h1>
        <Box>
          {
            <Tabs
              value={selectedCabin}
              onChange={(event, newValue) => {
                setSelectedCabin(newValue);
              }}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="scrollable auto tabs example"
            >
              {availableGuestCabins?.map((cabin) => (
                <Tab
                  key={cabin.category_id + "-" + cabin.room_id}
                  className="!font-bold"
                  value={cabin.category_id + "-" + cabin.room_id}
                  label={cabin.category_name + " - " + cabin.room_no}
                />
              ))}
            </Tabs>
          }
        </Box>
        <TableContainer component={Paper}>
          <Table size="small" aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell className="!font-bold">Guest Name</TableCell>
                <TableCell className="!font-bold">Price</TableCell>
                <TableCell className="!font-bold">Quantity</TableCell>
                <TableCell className="!font-bold">Check</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className="">
              {guestsAsPerCabin?.map((guest) => {
                if (
                  ((supplement.Selection_method === "custom 1" &&
                    supplement.child.Selection_method !== "custom 2") ||
                    supplement.Selection_method === "custom 3" ||
                    supplement.Selection_method === "custom 5") &&
                  getGuestAge(guest.dob, bookingDetails.sailing_date) <= 13
                )
                  return null;
                return (
                  guest && (
                    <TableRow key={guest.id}>
                      <TableCell>
                        <span className="font-bold text-mv-darkblue">
                          {guest?.first_name + " " + guest?.last_name + " "}{" "}
                        </span>{" "}
                        ({getGuestAge(guest.dob, bookingDetails.sailing_date)}{" "}
                        years)
                      </TableCell>
                      <TableCell className="text-nowrap">
                        ${" "}
                        {formatUSAmount(
                          getGuestAge(guest.dob, bookingDetails.sailing_date) >
                            13
                            ? supplement.farecode_price
                            : supplement.child.farecode_price ||
                                supplement.farecode_price
                        )}
                      </TableCell>
                      <TableCell>
                        {getGuestAge(guest.dob, bookingDetails.sailing_date) >
                          13 ||
                        supplement.child.farecode_price === undefined ? (
                          <div className="w-full flex flex-col items-center">
                            <div className="flex items-center mb-2">
                              <button
                                className="px-2 text-xl"
                                disabled={
                                  adults.find(
                                    (item) => item.guest_id === guest.id
                                  )?.quantity === min_qty
                                }
                                onClick={() =>
                                  decrement(
                                    setAdults,
                                    adults.find(
                                      (item) => item.guest_id === guest.id
                                    )?.quantity || min_qty,
                                    guest.id
                                  )
                                }
                              >
                                {" "}
                                -{" "}
                              </button>
                              <span className="px-2 text-mv-darkblue font-bold text-2xl">
                                {adults.find(
                                  (item) => item.guest_id === guest.id
                                )?.quantity || min_qty}
                              </span>
                              <button
                                className="px-2 text-xl"
                                disabled={
                                  adults.find(
                                    (item) => item.guest_id === guest.id
                                  )?.quantity === max_qty
                                }
                                onClick={() =>
                                  increment(
                                    setAdults,
                                    adults.find(
                                      (item) => item.guest_id === guest.id
                                    )?.quantity || min_qty,
                                    guest.id
                                  )
                                }
                              >
                                +
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full flex flex-col items-center">
                            <div className="flex items-center mb-2">
                              <button
                                className="px-2 text-xl"
                                disabled={
                                  children.find(
                                    (item) => item.guest_id === guest.id
                                  )?.quantity === child_min_qty
                                }
                                onClick={() =>
                                  decrement(
                                    setChildren,
                                    children.find(
                                      (item) => item.guest_id === guest.id
                                    )?.quantity || child_min_qty,
                                    guest.id
                                  )
                                }
                              >
                                -
                              </button>
                              <span className="px-2 text-mv-darkblue font-bold text-2xl">
                                {children.find(
                                  (item) => item.guest_id === guest.id
                                )?.quantity ||
                                  child_min_qty ||
                                  min_qty}
                              </span>
                              <button
                                className="px-2 text-xl"
                                disabled={
                                  children.find(
                                    (item) => item.guest_id === guest.id
                                  )?.quantity === child_max_qty
                                }
                                onClick={() =>
                                  increment(
                                    setChildren,
                                    children.find(
                                      (item) => item.guest_id === guest.id
                                    )?.quantity ||
                                      child_min_qty ||
                                      min_qty,
                                    guest.id
                                  )
                                }
                              >
                                +
                              </button>
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {
                          <Checkbox
                            key={guest.id}
                            checked={
                              adults.find((item) => item.guest_id === guest.id)
                                ?.checked ||
                              children.find(
                                (item) => item.guest_id === guest.id
                              )?.checked ||
                              false
                            }
                            onChange={(e) =>
                              handleCheckboxChange(
                                guest.id,
                                getGuestAge(
                                  guest.dob,
                                  bookingDetails.sailing_date
                                ) <= 13 && supplement.child.farecode_price
                                  ? setChildren
                                  : setAdults,
                                e.target.checked
                              )
                            }
                          />
                        }
                      </TableCell>
                    </TableRow>
                  )
                );
              })}
              <TableRow className="bg-mv-lightblue text-center flex">
                <TableCell className="!font-bold !text-xl">SubTotal</TableCell>
                <TableCell className="!font-bold !text-xl">
                  {" "}
                  {checkedSuppDetails[selectedCabin]?.subtotal?.toFixed(2)}
                </TableCell>
                <TableCell className="!font-bold !text-xl !text-center">
                  {checkedSuppDetails[selectedCabin]?.quantity}
                </TableCell>
                <TableCell className="!font-bold !text-xl"> </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <div className="text-center my-4 py-4 bg-gray-200">
          <p className="text-xl font-semibold">
            Total: $ {formatUSAmount(checkedSuppDetails.total)}
          </p>
        </div>
        <div className="flex justify-center py-4 space-x-2">
          <button
            onClick={() => setShowAddToCartModal(false)}
            className="px-4 py-2 border border-mv-darkblue text-mv-darkblue hover:bg-blue-600 hover:text-white transition duration-300"
          >
            KEEP SHOPPING
          </button>
          <button
            onClick={handleAddToCart}
            className="px-4 py-2 uppercase bg-mv-darkblue text-white hover:bg-blue-700 transition duration-300"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToCartModal;
